<template>
  <div class="expense-details-body">
    <ExpenseBodyHeader
      :expense="expense"
      :can-save="canEdit"
      :can-reopen="canReopen"
      @export-as-pdf="exportAsPdf()"
      @save-expense="saveExpense()"
      @reopen-expense="reopenExpense()"
    />
    <ExpenseDetailsTable
      :expense="modifiedExpense"
      :can-edit="canEdit"
      @change="onExpenseChange"
      @removeTableEntry="removeTableEntry"
    />

    <div v-if="canEdit" class="expense-minor-actions">
      <TolButton
        :label="$t('expense.actions.add')"
        button-type="minor"
        icon-name="plus"
        @click="addExpenseEntry()"
      />
    </div>
    <div v-if="canSubmit" class="expense-actions contractor-actions">
      <div class="spacer" />
      <TolButton
        :label="$t('expense.actions.submit')"
        button-type="major"
        class="submit-button"
        icon-name="check"
        @click="submitExpense()"
      />
    </div>
    <div v-else-if="canApproveOrReject" class="expense-actions approver-actions">
      <textarea
        v-model="approverComment"
        :class="{ 'approver-error': approverCommentError }"
        :placeholder="$t('expense.placeholders.commentsPlaceholder')"
        class="approver-comment"
        @focus="approverCommentError = false"
      />
      <div class="expense-major-actions">
        <div v-if="canApprove && expense.status !== 'APPROVED'">
          <TolButton
            :label="$t('expense.actions.approve')"
            button-type="major"
            icon-name="check"
            class="approve-button"
            @click="approverAction('APPROVE')"
          />
        </div>
        <div v-if="canReject && expense.status !== 'REJECTED'">
          <TolButton
            :label="$t('expense.actions.reject')"
            button-type="major"
            class="reject-button"
            icon-name="times"
            @click="approverAction('REJECT')"
          />
        </div>
        <div v-if="canProcess">
          <TolButton
            :label="$t('expense.actions.process')"
            button-type="major"
            class="process-button"
            icon-name="times"
            @click="approverAction('PROCESS')"
          />
        </div>
      </div>
    </div>

    <div v-if="canEdit" class="expense-minor-actions">
      <TolButton
        :tooltip="$t('expense.actions.save')"
        button-type="minor"
        icon-name="pencil-alt"
        class="action-round"
        @click="saveExpense"
      />
    </div>

    <div v-if="canReopen" class="expense-minor-actions">
      <TolButton
        :tooltip="$t('expense.actions.reopen')"
        button-type="minor"
        icon-name="lock-open"
        class="action-round"
        @click="reopenExpense"
      />
    </div>

    <div v-if="canViewAttachment && expense && expense.referenceNumber">
      <Attachment
        :reference-number="expense.referenceNumber"
        owner="EXPENSE_REPORT"
        @attachment-success="refreshAttachments"
      />
      <AttachmentsList
        attachment-type="EXPENSE_REPORT"
        :attachments="attachments"
        :attachment-options="attachmentOptions"
        @delete-attachment="confirmAttachmentDelete"
        @refresh="refreshAttachments"
      />
    </div>

    <div class="expense-history">
      <tol-button
        button-type="major"
        :label="showHideRecords"
        :icon-name="iconFor"
        @click="toggleHistoryRecordList()"
      />
    </div>

    <div v-if="showHideHistoryRecords" class="expense-history">
      <div class="sub-header">{{ $t('expense.history.title') }}</div>
      <hr />
      <ExpenseHistoryList />
    </div>
    <update-modal v-if="isLoading">
      <template v-slot:modalData>
        <vue-simple-spinner size="massive" />
      </template>
    </update-modal>
    <ConfirmModal
      v-if="isDeleteAttachmentModalDisplayed"
      action="DELETE_ATTACHMENT"
      :title="$t('attachments.confirmDeleteTitle')"
      @continue-action="deleteAttachment()"
      @cancel-action="cancelDeletingAttachment()"
    >
      <template v-slot:modalMessage>
        {{ $t('attachments.confirmDelete') }}
      </template>
    </ConfirmModal>
  </div>
</template>

<script>
import ExpenseBodyHeader from './ExpenseBodyHeader';
import { attachmentService } from '@/app/components/attachments/attachment.service';
import ConfirmModal from '@/app/components/ConfirmModal';
import UpdateModal from '@/app/components/UpdateModal';
import TolButton from '@/app/components/TolButton';
import { ExpenseDetailsActions, ExpenseDetailsGetters } from '../expense-details.keys';
import { expenseDetailsService } from '../expense-details.service';
import ExpenseDetailsTable from './ExpenseDetailsTable';
import ExpenseHistoryList from './ExpenseHistoryList';
import * as R from 'ramda';
import Attachment from '@/app/components/attachments/Attachment';
import AttachmentsList from '@/app/components/attachments/AttachmentsList';
import { TolErrorsActions } from '@/app/features/tol-errors/tol-errors.keys';
import { AppActions } from '@/app/app.keys';

export default {
  name: 'ExpenseDetailsBody',
  components: {
    Attachment,
    AttachmentsList,
    ConfirmModal,
    ExpenseBodyHeader,
    ExpenseDetailsTable,
    TolButton,
    ExpenseHistoryList,
    UpdateModal
  },
  props: {
    expense: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      approverComment: '',
      approverCommentError: false,
      clonedExpense: {},
      attachmentOptions: ['Receipt', 'Other'],
      showHideHistoryRecords: false,
      isDeleteAttachmentModalDisplayed: false,
      selectedAttachmentId: null
    };
  },
  computed: {
    showHideRecords() {
      return this.showHideHistoryRecords
        ? this.$t('expenses.details.hideHistory')
        : this.$t('expenses.details.showHistory');
    },
    iconFor() {
      return this.showHideHistoryRecords ? 'eye' : 'eye-slash';
    },
    isLoading() {
      return this.$store.getters[ExpenseDetailsGetters.IsLoading];
    },
    isApprover() {
      return this.currentUser !== null && this.currentUser.isApprover;
    },
    currentUser() {
      return this.$store.state.currentUser;
    },
    expenseStatus() {
      return R.path(['status'], this.expense);
    },
    isExpensePermitted() {
      return (
        this.currentUser &&
        !!this.currentUser.permissions &&
        !!this.currentUser.permissions['EXPENSE_REPORT']
      );
    },
    canEdit() {
      return (
        this.isExpensePermitted &&
        (!!this.currentUser.permissions['EXPENSE_REPORT']['SAVE'] &&
          R.contains(this.expenseStatus, this.currentUser.permissions['EXPENSE_REPORT']['SAVE']))
      );
    },
    canReopen() {
      return (
        this.isExpensePermitted &&
        (!!this.currentUser.permissions['EXPENSE_REPORT']['REOPEN'] &&
          R.contains(this.expenseStatus, this.currentUser.permissions['EXPENSE_REPORT']['REOPEN']))
      );
    },
    canSubmit() {
      return (
        this.isExpensePermitted &&
        (!!this.currentUser.permissions['EXPENSE_REPORT']['SUBMIT'] &&
          R.contains(this.expenseStatus, this.currentUser.permissions['EXPENSE_REPORT']['SUBMIT']))
      );
    },
    canViewAttachment() {
      return (
        this.isExpensePermitted && this.currentUser.permissions['EXPENSE_REPORT']['VIEW_ATTACHMENT']
      );
    },
    canApprove() {
      if (
        this.expense.assignment.stakeholderClientContacts.some(
          item => item.user_id === this.currentUser.id
        )
      ) {
        const [approver] = this.expense.assignment.stakeholderClientContacts.filter(
          item => item.user_id === this.currentUser.id
        );
        return approver.can_approve_time_sheets;
      } else {
        return (
          !!this.currentUser &&
          !!this.currentUser.permissions['TIME_SHEET']['APPROVE'] &&
          R.contains(this.expenseStatus, this.currentUser.permissions['TIME_SHEET']['APPROVE'])
        );
      }
    },
    canReject() {
      if (
        this.expense.assignment.stakeholderClientContacts.some(
          item => item.user_id === this.currentUser.id
        )
      ) {
        const [approver] = this.expense.assignment.stakeholderClientContacts.filter(
          item => item.user_id === this.currentUser.id
        );
        return approver.can_approve_time_sheets;
      } else {
        return (
          !!this.currentUser &&
          !!this.currentUser.permissions['TIME_SHEET']['REJECT'] &&
          R.contains(this.expenseStatus, this.currentUser.permissions['TIME_SHEET']['REJECT'])
        );
      }
    },
    canApproveOrReject() {
      return (
        this.isExpensePermitted &&
        ((this.currentUser.permissions['EXPENSE_REPORT']['APPROVE'] &&
          R.contains(
            this.expenseStatus,
            this.currentUser.permissions['EXPENSE_REPORT']['APPROVE']
          )) ||
          (this.currentUser.permissions['EXPENSE_REPORT']['REJECT'] &&
            R.contains(
              this.expenseStatus,
              this.currentUser.permissions['EXPENSE_REPORT']['REJECT']
            )))
      );
    },
    canProcess() {
      return (
        !!this.currentUser &&
        !!this.currentUser.permissions['EXPENSE_REPORT']['PROCESS'] &&
        R.contains(this.expenseStatus, this.currentUser.permissions['EXPENSE_REPORT']['PROCESS'])
      );
    },
    attachments() {
      return this.$store.state.expensesState.expenseDetailsState.attachments;
    },
    modifedExpenseEntries() {
      return this.clonedExpense.expenseEntries;
    },
    modifiedExpense() {
      return {
        ...this.clonedExpense
      };
    }
  },
  watch: {
    expense: {
      immediate: true,
      handler() {
        this.clonedExpense = R.clone(this.expense);
      }
    }
  },
  mounted() {
    this.refreshAttachments();

    if (this.canEdit || this.canSubmit) {
      this.$store.dispatch(AppActions.SetPageTitle, this.$t('expenses.details.editTitle'));
    } else if (this.canApproveOrReject) {
      this.$store.dispatch(AppActions.SetPageTitle, this.$t('expenses.details.reviewTitle'));
    }
  },
  methods: {
    toggleHistoryRecordList() {
      this.showHideHistoryRecords = !this.showHideHistoryRecords;
      if (this.showHideHistoryRecords) {
        this.$store.dispatch(
          ExpenseDetailsActions.RetrieveExpenseHistory,
          this.expense.referenceNumber
        );
      }
    },
    onExpenseChange({ entries, totalAmount, expenseNote }) {
      this.clonedExpense.expenseEntries = entries;
      this.clonedExpense.totalAmount = totalAmount;
      this.clonedExpense.expenseNote = expenseNote;
    },
    expenseAction(actionRequest) {
      this.$store.dispatch(ExpenseDetailsActions.SaveExpense, actionRequest);
    },
    saveExpense() {
      this.expenseAction({
        action: 'SAVE',
        entries: this.clonedExpense.expenseEntries,
        totalAmount: this.clonedExpense.totalAmount,
        expenseNote: this.clonedExpense.expenseNote
      });
    },
    reopenExpense() {
      this.expenseAction({
        action: 'REOPEN',
        entries: this.clonedExpense.expenseEntries
      });
    },
    submitExpense() {
      this.expenseAction({
        action: 'SUBMIT',
        entries: this.clonedExpense.expenseEntries,
        totalAmount: this.clonedExpense.totalAmount,
        expenseNote: this.clonedExpense.expenseNote
      });
    },
    addExpenseEntry() {
      this.clonedExpense.expenseEntries.push({
        entryNote: '',
        amount: 0,
        entryDate: null,
        isUnit: false,
        unitQuantity: 0,
        unitPrice: 0
      });
    },
    removeTableEntry(idx) {
      this.clonedExpense.expenseEntries.splice(idx, 1);
    },
    approverAction(action) {
      if (action === 'REJECT' && this.isNilOrEmpty(this.approverComment)) {
        this.approverCommentError = true;
        this.$store.dispatch(TolErrorsActions.SetError, {
          message: this.$t('expenses.details.errors.commentMandatory')
        });
      } else {
        this.approverCommentError = false;
        this.$store.dispatch(ExpenseDetailsActions.ExpenseApproverAction, {
          action,
          approverComment: this.approverComment
        });
      }
    },
    isNilOrEmpty(value) {
      return R.isNil(value) || R.isEmpty(value);
    },
    exportAsPdf() {
      expenseDetailsService.downloadExpensePdf(this.expense.referenceNumber);
    },
    confirmAttachmentDelete(attachmentId) {
      this.selectedAttachmentId = attachmentId;
      this.isDeleteAttachmentModalDisplayed = true;
    },
    async deleteAttachment() {
      await attachmentService.deleteAttachment(this.selectedAttachmentId);
      this.isDeleteAttachmentModalDisplayed = false;
      this.selectedAttachmentId = null;
      this.refreshAttachments();
    },
    cancelDeletingAttachment() {
      this.isDeleteAttachmentModalDisplayed = false;
      this.selectedAttachmentId = null;
    },
    refreshAttachments() {
      this.$store.dispatch(ExpenseDetailsActions.RetrieveExpenseAttachments, this.$route.params.id);
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../../../styles/colors.scss';
@import '../../../../../styles/variables.scss';

.expense-details-body {
  background-color: $background-level-1;

  .expense-actions {
    min-height: 136px;
    background-color: $background-level-2;
    display: flex;
    padding-top: 12px;
    padding-bottom: 12px;
    .spacer {
      flex-grow: 1;
    }
  }
  .submit-button {
    margin: 32px -42px 32px 0px;
  }
  .expense-major-actions,
  .expense-minor-actions {
    display: inline-block;
    vertical-align: top;
  }
  .expense-major-actions {
    margin-right: -42px;
  }

  .expense-minor-actions {
    width: 100%;
    background: $white;
    padding: $details-padding;
    text-align: right;
    border-top: 1px solid $grey;
  }

  .process-button {
    margin-top: 16px;
  }
  .approve-button {
    margin-bottom: 16px;
  }
  .approver-actions {
    background-color: $background-level-2;
    padding: 20px 60px;
  }
  .contractor-approver-comment.approver-actions {
    justify-content: flex-start;
  }
  .approver-comment {
    height: 160px;
    width: 352px;
    border: 1px solid #d8d8d8;
    &::placeholder {
      color: $text-color-light;
    }
  }
  .approver-error {
    border: 2px solid red;
  }
  .attachment-upload {
    margin-top: 10px;
  }

  .expense-history {
    background-color: $background-level-2;
    margin: 10px auto;

    .sub-header {
      padding: $details-padding;
    }
  }
}

@media print {
  .expense-details-body {
    .expense-actions,
    .submit-button,
    .approve-button,
    .expense-major-actions,
    .expense-minor-actions,
    .approver-actions,
    .approver-comment {
      display: none;
    }
  }
}

@media only screen and (max-width: 768px) {
  .expense-history {
    display: none;
  }
  .expense-details-body {
    .approver-comment {
      width: 100%;
    }
  }

  .expense-actions.approver-actions {
    padding: $responsive-details-padding;
  }
}
</style>
